import React, { useMemo, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { DataTable } from '@uhg-abyss/web/ui/DataTable';
import { useDataTable } from '@uhg-abyss/web/hooks/useDataTable';
import { useParams } from '@uhg-abyss/web/tools/reactRouterTools';
import { LagoonContext } from '@src/AppContext';

export const SchemaTable = ({ currentApi }) => {
  const { service } = useParams();
  const lagoonContext = React.useContext(LagoonContext);
  const featureToggles = lagoonContext.getTable('configs');
  const serviceDescriptionToggle = featureToggles?.getRow(
    'SERVICE_DESCRIPTION_TOGGLE'
  )?.value;
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10,
  });
  const [expanded, setExpanded] = useState({
    0: true,
  });
  const asteriskText = (key) => {
    return (
      <span>
        {key}
        <span style={{ color: '#C40000' }}> *</span>
      </span>
    );
  };

  const col = [
    {
      header: 'Field',
      accessorKey: 'field',
      cell: (info) => {
        return info.getValue();
      },
    },
    {
      header: 'Type',
      accessorKey: 'type',
      cell: (info) => {
        return info.getValue();
      },
      maxWidth: 50,
    },
  ];
  if (serviceDescriptionToggle?.includes(service)) {
    col.push({
      header: 'Description',
      accessorKey: 'description',
      cell: (info) => {
        return info.getValue();
      },
    });
  }

  const columns = React.useMemo(() => {
    return col;
  }, [service, serviceDescriptionToggle]);

  const addHierarchyIds = (rows, parentId = '') => {
    return rows.map((row, index) => {
      const uniqueId = parentId ? `${parentId}-${index}` : `${index}`;
      const nextSubRows = Array.isArray(row?.subRows)
        ? addHierarchyIds(row.subRows, uniqueId)
        : undefined;

      return {
        ...row,
        uniqueId,
        ...(nextSubRows ? { subRows: nextSubRows } : {}),
      };
    });
  };

  const formattedSchema = (data) => {
    if (!data) return [];
    const schemaData = Object.keys(data).map((key) => {
      const value = data[key];
      let { type } = value;
      if (data[key].type === 'object') {
        type = 'object';
        const subRows = formattedSchema(value.properties);
        return {
          field: data[key].required ? asteriskText(key) : key,
          type,
          subRows,
          description: value?.description?.substring(0, 350),
        };
      }
      if (data[key].type === 'array') {
        type = 'array';
        const subRows = formattedSchema(value?.items?.properties);
        return {
          field: data[key].required ? asteriskText(key) : key,
          type,
          subRows,
          description: value?.description?.substring(0, 350),
        };
      }
      return {
        field: data[key].required ? asteriskText(key) : key,
        type,
        description: value?.description?.substring(0, 350),
      };
    });
    return schemaData;
  };

  const data = useMemo(() => {
    return addHierarchyIds(formattedSchema(currentApi));
  }, [currentApi]);

  const dataTableProps = useDataTable({
    initialData: data,
    initialColumns: columns,
    expandColumnConfig: { expandMode: 'subRows' },
    paginationConfig: {
      enablePagination: true,
      pageSizeOptions: [10, 25, 50, 100],
    },
    tableConfig: {
      paginateExpandedRows: true,
      state: {
        pagination,
        expanded,
      },
      onPaginationChange: setPagination,
      onExpandedChange: setExpanded,
    },
  });

  useEffect(() => {
    dataTableProps.setData(data, true);
  }, [data]);

  useEffect(() => {
    const pageCount = dataTableProps?.tableInstance?.getPageCount?.() || 0;
    const lastValidPageIndex = Math.max(pageCount - 1, 0);

    setPagination((prev) => {
      if (prev.pageIndex > lastValidPageIndex) {
        return {
          ...prev,
          pageIndex: 0,
        };
      }

      return prev;
    });
  }, [expanded, data, pagination.pageSize, dataTableProps]);

  return (
    <DataTable
      tableState={dataTableProps}
      css={{
        '.abyss-data-table-cell': {
          fontSize: '14px',
        },
        '.abyss-data-table-header-data': {
          fontSize: '14px',
          color: '#212223',
          fontWeight: 700,
        },
      }}
    >
      <DataTable.SlotWrapper
        css={{
          overflowX: 'auto',
          'abyss-data-table-slot-wrapper': {
            display: 'flex',
            flexDirection: 'row-reverse',
            justifyContent: 'space-between',
          },
        }}
      >
        <DataTable.Pagination showPageSizeDropdown variant="minimal" />
      </DataTable.SlotWrapper>
      <DataTable.Table />
      <DataTable.SlotWrapper
        css={{
          overflowX: 'auto',
          '.abyss-pagination-results-text': {
            fontSize: '14px',
          },
        }}
      >
        <DataTable.Pagination />
      </DataTable.SlotWrapper>
    </DataTable>
  );
};

SchemaTable.propTypes = {
  currentApi: PropTypes.shape({}).isRequired,
};
